import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, Calendar, DollarSign } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { AppLayout } from '../../../components/layout/AppLayout';
import { pickupAPI } from '../../../services/api';
import { toast } from 'sonner';

const PostDeliveryPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    service_category: 'delivery',  // NEW: Service category
    task_description: '',          // NEW: Task description
    pickup_country: '',
    pickup_city: '',
    pickup_area: '',
    destination_country: '',
    destination_city: '',
    destination_area: '',
    package_type: 'other',
    package_description: '',
    delivery_fee: '',
    pickup_date: '',
    special_instructions: '',
    exact_pickup_address: '',
    exact_delivery_address: '',
    recipient_name: '',
    recipient_phone: ''
  });

  const serviceCategories = [
    { value: 'delivery', label: 'Delivery', description: 'Send parcels, documents, or items' },
    { value: 'shopping_assistance', label: 'Shopping Assistance', description: 'Shop and deliver items for you' },
    { value: 'document_collection', label: 'Document Collection', description: 'Pick up and deliver documents' },
    { value: 'errands', label: 'Personal Errands', description: 'General errands and tasks' },
    { value: 'small_transport', label: 'Small Transport', description: 'Transport small items' }
  ];

  const packageTypes = [
    { value: 'document', label: 'Document' },
    { value: 'small_parcel', label: 'Small Parcel' },
    { value: 'medium_parcel', label: 'Medium Parcel' },
    { value: 'large_parcel', label: 'Large Parcel' },
    { value: 'clothing', label: 'Clothing' },
    { value: 'personal_items', label: 'Personal Items' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.service_category) {
      toast.error('Please select a service category');
      return;
    }
    if (!formData.task_description || formData.task_description.trim().length < 10) {
      toast.error('Please describe what you need (at least 10 characters)');
      return;
    }
    if (!formData.pickup_country || !formData.pickup_city) {
      toast.error('Please provide pickup location');
      return;
    }
    if (!formData.destination_country || !formData.destination_city) {
      toast.error('Please provide destination location');
      return;
    }
    if (!formData.delivery_fee || parseFloat(formData.delivery_fee) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    if (!formData.pickup_date) {
      toast.error('Please select date');
      return;
    }

    setLoading(true);
    try {
      const response = await pickupAPI.createDelivery({
        ...formData,
        delivery_fee: parseFloat(formData.delivery_fee)
      });
      
      toast.success('Job request posted successfully!');
      toast.info(`Your job code: ${response.data.delivery_code} (save this!)`);
      
      // Navigate to job details
      navigate(`/pickup/delivery/${response.data.delivery_id}`);
    } catch (error) {
      console.error('Post job error:', error);
      toast.error(error.response?.data?.detail || 'Failed to post job request');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Request a Job</h1>
            <p className="text-muted-foreground">Post your request and connect with available runners</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Service Category */}
          <div className="space-y-4 p-6 bg-muted/50 rounded-lg">
            <div>
              <Label>Service Type *</Label>
              <Select value={formData.service_category} onValueChange={(value) => handleChange('service_category', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent>
                  {serviceCategories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <div>
                        <div className="font-medium">{cat.label}</div>
                        <div className="text-xs text-muted-foreground">{cat.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Describe What You Need *</Label>
              <Textarea 
                placeholder="E.g., Pick up 2 bags of groceries from Pick n Pay and deliver to my home..."
                value={formData.task_description}
                onChange={(e) => handleChange('task_description', e.target.value)}
                rows={4}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Be specific about what you need help with (minimum 10 characters)
              </p>
            </div>
          </div>

          {/* Pickup Location */}
          <div className="bg-card border rounded-lg p-6 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <MapPin className="h-5 w-5 text-blue-600" />
              Pickup Location
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pickup_country">Country *</Label>
                <Input
                  id="pickup_country"
                  placeholder="e.g., South Africa"
                  value={formData.pickup_country}
                  onChange={(e) => handleChange('pickup_country', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="pickup_city">City *</Label>
                <Input
                  id="pickup_city"
                  placeholder="e.g., Johannesburg"
                  value={formData.pickup_city}
                  onChange={(e) => handleChange('pickup_city', e.target.value)}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="pickup_area">Area / Suburb (Optional)</Label>
              <Input
                id="pickup_area"
                placeholder="e.g., Sandton, Rosebank"
                value={formData.pickup_area}
                onChange={(e) => handleChange('pickup_area', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">General area - exact address will be shared after driver accepts</p>
            </div>
            
            <div>
              <Label htmlFor="exact_pickup_address">Exact Pickup Address (Private)</Label>
              <Input
                id="exact_pickup_address"
                placeholder="Full street address"
                value={formData.exact_pickup_address}
                onChange={(e) => handleChange('exact_pickup_address', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">🔒 Only visible to assigned runner</p>
            </div>
          </div>

          {/* Destination Location */}
          <div className="bg-card border rounded-lg p-6 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <MapPin className="h-5 w-5 text-green-600" />
              Destination Location
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="destination_country">Country *</Label>
                <Input
                  id="destination_country"
                  placeholder="e.g., Zimbabwe"
                  value={formData.destination_country}
                  onChange={(e) => handleChange('destination_country', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="destination_city">City *</Label>
                <Input
                  id="destination_city"
                  placeholder="e.g., Harare"
                  value={formData.destination_city}
                  onChange={(e) => handleChange('destination_city', e.target.value)}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="destination_area">Area / Suburb (Optional)</Label>
              <Input
                id="destination_area"
                placeholder="General destination area"
                value={formData.destination_area}
                onChange={(e) => handleChange('destination_area', e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="exact_delivery_address">Exact Delivery Address (Private)</Label>
              <Input
                id="exact_delivery_address"
                placeholder="Full street address"
                value={formData.exact_delivery_address}
                onChange={(e) => handleChange('exact_delivery_address', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">🔒 Only visible to assigned runner</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="recipient_name">Recipient Name (Optional)</Label>
                <Input
                  id="recipient_name"
                  placeholder="Person receiving the parcel"
                  value={formData.recipient_name}
                  onChange={(e) => handleChange('recipient_name', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="recipient_phone">Recipient Phone (Optional)</Label>
                <Input
                  id="recipient_phone"
                  placeholder="+27..."
                  value={formData.recipient_phone}
                  onChange={(e) => handleChange('recipient_phone', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Package Details */}
          <div className="bg-card border rounded-lg p-6 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <Package className="h-5 w-5" />
              Package Details
            </div>
            
            <div>
              <Label htmlFor="package_type">Package Type *</Label>
              <Select value={formData.package_type} onValueChange={(value) => handleChange('package_type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select package type" />
                </SelectTrigger>
                <SelectContent>
                  {packageTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="package_description">Package Description (Optional)</Label>
              <Textarea
                id="package_description"
                placeholder="Describe what you're sending"
                value={formData.package_description}
                onChange={(e) => handleChange('package_description', e.target.value)}
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="special_instructions">Special Instructions (Optional)</Label>
              <Textarea
                id="special_instructions"
                placeholder="Any special handling or delivery instructions"
                value={formData.special_instructions}
                onChange={(e) => handleChange('special_instructions', e.target.value)}
                rows={2}
              />
            </div>
          </div>

          {/* Delivery Details */}
          <div className="bg-card border rounded-lg p-6 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <Calendar className="h-5 w-5" />
              Delivery Details
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pickup_date">Pickup Date *</Label>
                <Input
                  id="pickup_date"
                  type="date"
                  value={formData.pickup_date}
                  onChange={(e) => handleChange('pickup_date', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div>
                <Label htmlFor="delivery_fee">Your Offer (ZAR) *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="delivery_fee"
                    type="number"
                    placeholder="300"
                    value={formData.delivery_fee}
                    onChange={(e) => handleChange('delivery_fee', e.target.value)}
                    className="pl-10"
                    min="1"
                    step="0.01"
                    required
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Runners can accept or make counteroffers</p>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button type="button" variant="outline" onClick={() => navigate(-1)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700">
              {loading ? 'Posting...' : 'Request Job'}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
};

export default PostDeliveryPage;